/**
 * View component for displaying leads where the current user is a member
 * Shows only leads where the user has been added as a team member
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Lead } from '../../types/Lead';
import { starService } from '../../services/starService';
import { useAuth } from '../../contexts/AuthContext';
import { useSortedLeads } from '../../hooks/useSortedLeads';

import LeadCard from '../leads/LeadCard';
import { Pagination } from '../ui/Pagination';

interface MyLeadsViewProps {
  allLeads: Lead[];
  onLeadToggle: (leadId: string) => void;
  expandedCards: Set<string>;
  memberLeadIds: Set<string>;
  onMemberChange: (leadId: string, isMember: boolean) => void;
  onStarChange?: (leadId: string, isStarred: boolean) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalPages: number;
  itemsPerPage: number;
}

export const MyLeadsView: React.FC<MyLeadsViewProps> = ({
  allLeads,
  onLeadToggle,
  expandedCards,
  memberLeadIds,
  onMemberChange,
  onStarChange,
  currentPage,
  onPageChange,
  totalPages,
  itemsPerPage
}) => {
  const { user } = useAuth();
  const [starredLeadIds, setStarredLeadIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Load starred status for member leads
  const loadStarredStatus = useCallback(async () => {
    if (!user) {
      setStarredLeadIds(new Set());
      setIsLoading(false);
      return;
    }

    try {
      const starredLeadsData = await starService.getStarredLeads(user.uid);
      setStarredLeadIds(new Set(starredLeadsData.map(star => star.leadId)));
    } catch (err) {
      console.error('Failed to load starred status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // If there are no leads at all, don't show loading state
    if (allLeads.length === 0) {
      setIsLoading(false);
      return;
    }
    loadStarredStatus();
  }, [loadStarredStatus, allLeads.length]);

  // Handle star changes
  const handleStarChange = useCallback(async (leadId: string, isStarred: boolean) => {
    if (isStarred) {
      setStarredLeadIds(prev => {
        const newSet = new Set(prev);
        newSet.add(leadId);
        return newSet;
      });
    } else {
      setStarredLeadIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(leadId);
        return newSet;
      });
    }

    // Notify Dashboard about star change
    onStarChange?.(leadId, isStarred);
  }, [onStarChange]);

  // Filter leads to only show where user is a member
  const filteredLeads = allLeads.filter(lead =>
    lead.id && memberLeadIds.has(lead.id)
  );

  // Sort leads by custom weighted score (same as All Leads tab)
  const sortedLeads = useSortedLeads(filteredLeads);



  // Show loading only if we don't have member data yet
  if (memberLeadIds.size === 0 && isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '48px',
        color: '#6b7280'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '16px'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          Loading your leads...
        </div>
      </div>
    );
  }

  if (filteredLeads.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '48px',
        color: '#6b7280'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '32px',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '2px dashed #d1d5db',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
          <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            No Team Assignments
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            You haven't been added as a team member to any leads yet. Once you're added to a lead, it will appear here.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#f0f9ff',
        borderRadius: '8px',
        border: '1px solid #0ea5e9',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{ fontSize: '20px' }}>👥</span>
        <div>
          <div style={{ fontWeight: '600', color: '#0c4a6e' }}>
            My Leads ({sortedLeads.length})
          </div>
          <div style={{ fontSize: '14px', color: '#0369a1' }}>
            Leads where you are a team member
          </div>
        </div>
      </div>

      {sortedLeads.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((lead) => (
        <LeadCard
          key={lead.id}
          lead={lead}
          isExpanded={expandedCards.has(lead.id || '')}
          onToggle={() => lead.id && onLeadToggle(lead.id)}
          isStarred={lead.id ? starredLeadIds.has(lead.id) : false}
          onStarChange={(isStarred) => lead.id && handleStarChange(lead.id, isStarred)}
          onMemberChange={onMemberChange}
        />
      ))}

      {/* Pagination */}
      {sortedLeads.length > 0 && (
        <div style={{ marginTop: '48px' }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            totalItems={sortedLeads.length}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </div>
  );
};